import { useEffect, useRef, type MutableRefObject } from 'react';
import type { OrderLineAdapter, PlusClickParams, TradingViewWidget, WidgetOptions } from '../types/charting-library';
import type { PriceAlarm, WatchlistFlagColor } from '../types/api';
import type { ChartLayout } from '../types/settings';
import { CUSTOM_INDICATOR_NAMES } from './customIndicators';
import { loadTradingViewLibrary } from './loadTradingViewLibrary';

interface UseTradingViewWidgetInput {
  options: WidgetOptions | null;
  onAutoSave(layout: ChartLayout): void;
  onReady?(): void;
  onFlush?(): void;
  onSymbolChange?(symbol: string): void;
  onIntervalChange?(interval: string): void;
  onAddToWatchlist?(symbol: string): void;
  onRemoveFromWatchlist?(symbol: string): void;
  onToggleWatchlistShortcut?(symbol: string): Promise<boolean> | boolean;
  onToggleFlagShortcut?(symbol: string): Promise<boolean> | boolean;
  onSetFlagColor?(symbol: string, flagColor: WatchlistFlagColor | null): Promise<boolean> | boolean;
  onDeleteAlarm?(id: string): void;
  onPlusClick?(params: PlusClickParams): void;
  draftAlarmPrice?: number | null;
  onDraftAlarmPriceChange?(price: number): void;
  watchlistSymbolNames: ReadonlySet<string>;
  flaggedWatchlistSymbolNames: ReadonlySet<string>;
  flaggedWatchlistSymbolColors: Readonly<Record<string, WatchlistFlagColor>>;
  alarms: PriceAlarm[];
}

const STAR_OUTLINED = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
const STAR_FILLED = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>';
const BOOKMARK_OUTLINED = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>';
const BOOKMARK_FILLED = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>';
const flagColors: WatchlistFlagColor[] = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'];
const flagColorHex: Record<WatchlistFlagColor, string> = {
  red: '#ef4444',
  orange: '#f97316',
  yellow: '#eab308',
  green: '#22c55e',
  blue: '#3b82f6',
  purple: '#a855f7',
};

function updateWatchlistBtn(btn: HTMLButtonElement | null, symbol: string, watchlistNames: ReadonlySet<string>) {
  if (!btn) return;
  const inList = isSymbolInWatchlist(symbol, watchlistNames);
  applyWatchlistBtnState(btn, inList);
}

function applyWatchlistBtnState(btn: HTMLButtonElement | null, inList: boolean) {
  if (!btn) return;
  btn.innerHTML = inList ? STAR_FILLED : STAR_OUTLINED;
  btn.title = inList ? 'Remove from watchlist' : 'Add to watchlist';
  btn.classList.toggle('tv-watchlist-btn--active', inList);
  btn.style.color = inList ? '#f0b429' : '';
}

function updateFlagBtn(btn: HTMLButtonElement | null, symbol: string, flaggedColors: Readonly<Record<string, WatchlistFlagColor>>) {
  if (!btn) return;
  applyFlagBtnState(btn, findSymbolFlagColor(symbol, flaggedColors));
}

function applyFlagBtnState(btn: HTMLButtonElement | null, flagColor: WatchlistFlagColor | null) {
  if (!btn) return;
  btn.innerHTML = flagColor ? BOOKMARK_FILLED : BOOKMARK_OUTLINED;
  btn.title = flagColor ? `Flag: ${flagColor}` : 'Flag symbol';
  btn.classList.toggle('tv-flag-btn--active', Boolean(flagColor));
  btn.style.color = flagColor ? flagColorHex[flagColor] : '';
}

function findSymbolFlagColor(symbol: string, flaggedColors: Readonly<Record<string, WatchlistFlagColor>>): WatchlistFlagColor | null {
  const raw = symbol.trim();
  if (!raw) return null;
  if (flaggedColors[raw]) return flaggedColors[raw];
  const shortName = raw.includes(':') ? raw.split(':').pop() ?? raw : raw;
  if (flaggedColors[shortName]) return flaggedColors[shortName];
  const lowerRaw = raw.toLowerCase();
  const lowerShortName = shortName.toLowerCase();
  for (const [name, color] of Object.entries(flaggedColors)) {
    const lowerName = name.toLowerCase();
    if (lowerName === lowerRaw || lowerName === lowerShortName) return color;
  }
  return null;
}

function closeFlagMenu(menuRef: MutableRefObject<HTMLDivElement | null>) {
  menuRef.current?.remove();
  menuRef.current = null;
}

function toggleFlagMenu(btn: HTMLButtonElement, symbol: string, onSelect: (color: WatchlistFlagColor | null) => void, menuRef: MutableRefObject<HTMLDivElement | null>, boundary?: DOMRect) {
  if (menuRef.current) {
    closeFlagMenu(menuRef);
    return;
  }

  const rect = btn.getBoundingClientRect();
  const menu = document.createElement('div');
  menu.className = 'tv-flag-menu';
  menu.style.left = `${rect.left}px`;
  menu.style.top = `${rect.bottom + 8}px`;
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', `Flag ${symbol}`);

  for (const color of flagColors) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'tv-flag-menu__item';
    item.style.color = flagColorHex[color];
    item.title = color;
    item.setAttribute('aria-label', `Set ${color} flag`);
    item.innerHTML = BOOKMARK_FILLED;
    item.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeFlagMenu(menuRef);
      onSelect(color);
    });
    menu.appendChild(item);
  }

  const clear = document.createElement('button');
  clear.type = 'button';
  clear.className = 'tv-flag-menu__clear';
  clear.textContent = 'Clear';
  clear.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeFlagMenu(menuRef);
    onSelect(null);
  });
  menu.appendChild(clear);

  function handlePointerDown(event: PointerEvent) {
    if (menu.contains(event.target as Node) || btn.contains(event.target as Node)) return;
    closeFlagMenu(menuRef);
    window.removeEventListener('pointerdown', handlePointerDown);
    window.removeEventListener('keydown', handleKeyDown);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    closeFlagMenu(menuRef);
    window.removeEventListener('pointerdown', handlePointerDown);
    window.removeEventListener('keydown', handleKeyDown);
  }

  document.body.appendChild(menu);
  const margin = 8;
  const centeredLeft = rect.left + rect.width / 2 - menu.offsetWidth / 2;
  const minLeft = boundary ? boundary.left + margin : margin;
  const maxLeft = boundary ? boundary.right - menu.offsetWidth - margin : window.innerWidth - menu.offsetWidth - margin;
  const nextLeft = Math.min(Math.max(minLeft, centeredLeft), Math.max(minLeft, maxLeft));
  const nextTop = Math.min(Math.max(margin, rect.bottom + 20), Math.max(margin, window.innerHeight - menu.offsetHeight - margin));
  menu.style.left = `${nextLeft}px`;
  menu.style.top = `${nextTop}px`;
  menuRef.current = menu;
  window.setTimeout(() => window.addEventListener('pointerdown', handlePointerDown), 0);
  window.addEventListener('keydown', handleKeyDown);
}

function isSymbolInWatchlist(symbol: string, watchlistNames: ReadonlySet<string>): boolean {
  const raw = symbol.trim();
  if (!raw) return false;
  if (watchlistNames.has(raw)) return true;

  const shortName = raw.includes(':') ? raw.split(':').pop() ?? raw : raw;
  if (watchlistNames.has(shortName)) return true;

  const lowerRaw = raw.toLowerCase();
  const lowerShortName = shortName.toLowerCase();
  for (const name of watchlistNames) {
    const lowerName = name.toLowerCase();
    if (lowerName === lowerRaw || lowerName === lowerShortName) return true;
  }

  return false;
}

export function useTradingViewWidget({ options, onAutoSave, onReady, onFlush, onSymbolChange, onIntervalChange, onAddToWatchlist, onRemoveFromWatchlist, onToggleWatchlistShortcut, onToggleFlagShortcut, onSetFlagColor, onDeleteAlarm, onPlusClick, draftAlarmPrice, onDraftAlarmPriceChange, watchlistSymbolNames, flaggedWatchlistSymbolNames, flaggedWatchlistSymbolColors, alarms }: UseTradingViewWidgetInput) {
  const widgetRef = useRef<TradingViewWidget | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const watchlistBtnRef = useRef<HTMLButtonElement | null>(null);
  const flagBtnRef = useRef<HTMLButtonElement | null>(null);
  const alarmLinesRef = useRef<Map<string, OrderLineAdapter>>(new Map());
  const draftAlarmLineRef = useRef<OrderLineAdapter | null>(null);
  const onAutoSaveRef = useRef(onAutoSave);
  const onFlushRef = useRef(onFlush);
  const onReadyRef = useRef(onReady);
  const onSymbolChangeRef = useRef(onSymbolChange);
  const onIntervalChangeRef = useRef(onIntervalChange);
  const onAddToWatchlistRef = useRef(onAddToWatchlist);
  const onRemoveFromWatchlistRef = useRef(onRemoveFromWatchlist);
  const onToggleWatchlistShortcutRef = useRef(onToggleWatchlistShortcut);
  const onToggleFlagShortcutRef = useRef(onToggleFlagShortcut);
  const onSetFlagColorRef = useRef(onSetFlagColor);
  const onDeleteAlarmRef = useRef(onDeleteAlarm);
  const onPlusClickRef = useRef(onPlusClick);
  const onDraftAlarmPriceChangeRef = useRef(onDraftAlarmPriceChange);
  const watchlistNamesRef = useRef(watchlistSymbolNames);
  const flaggedNamesRef = useRef(flaggedWatchlistSymbolNames);
  const flaggedColorsRef = useRef(flaggedWatchlistSymbolColors);
  const watchlistShortcutPendingRef = useRef(false);
  const flagShortcutPendingRef = useRef(false);
  const optimisticFlagStateRef = useRef<{ symbol: string; flagColor: WatchlistFlagColor | null } | null>(null);
  const flagMenuRef = useRef<HTMLDivElement | null>(null);
  const optimisticShortcutStateRef = useRef<{ symbol: string; inList: boolean } | null>(null);

  onAutoSaveRef.current = onAutoSave;
  onFlushRef.current = onFlush;
  onReadyRef.current = onReady;
  onSymbolChangeRef.current = onSymbolChange;
  onIntervalChangeRef.current = onIntervalChange;
  onAddToWatchlistRef.current = onAddToWatchlist;
  onRemoveFromWatchlistRef.current = onRemoveFromWatchlist;
  onToggleWatchlistShortcutRef.current = onToggleWatchlistShortcut;
  onToggleFlagShortcutRef.current = onToggleFlagShortcut;
  onSetFlagColorRef.current = onSetFlagColor;
  onDeleteAlarmRef.current = onDeleteAlarm;
  onPlusClickRef.current = onPlusClick;
  onDraftAlarmPriceChangeRef.current = onDraftAlarmPriceChange;
  watchlistNamesRef.current = watchlistSymbolNames;
  flaggedNamesRef.current = flaggedWatchlistSymbolNames;
  flaggedColorsRef.current = flaggedWatchlistSymbolColors;

  useEffect(() => {
    if (!options) return undefined;
    let cancelled = false;

    void loadTradingViewLibrary().then(() => {
      if (cancelled) return;

      const widget = new TradingView.widget(options);
      widgetRef.current = widget;

      const persistLayout = () => {
        widget.save((layout) => onAutoSaveRef.current(layout));
      };

      const flushSettings = () => {
        onFlushRef.current?.();
      };
      const handlePlusClick = (params: PlusClickParams) => {
        onPlusClickRef.current?.(params);
      };

      widget.onChartReady(() => {
        if (cancelled) return;

        if (!options.saved_data) {
          widget.chart().createStudy('Ichimoku Cloud', false, false);
        }
        syncAlarmLines(widget, alarms, alarmLinesRef.current, (id) => onDeleteAlarmRef.current?.(id));

        widget.subscribe('onAutoSaveNeeded', persistLayout);
        if (onPlusClickRef.current) widget.subscribe('onPlusClick', handlePlusClick);
        if (onToggleWatchlistShortcutRef.current) {
          widget.onShortcut('alt+w', () => {
            if (watchlistShortcutPendingRef.current) return;
            const { symbol: currentSymbol } = widget.symbolInterval();
            const inList = isSymbolInWatchlist(currentSymbol, watchlistNamesRef.current);
            const nextInList = !inList;
            watchlistShortcutPendingRef.current = true;
            optimisticShortcutStateRef.current = { symbol: currentSymbol, inList: nextInList };
            applyWatchlistBtnState(watchlistBtnRef.current, nextInList);
            void Promise.resolve(onToggleWatchlistShortcutRef.current?.(currentSymbol))
              .then((ok) => {
                if (ok === false) {
                  optimisticShortcutStateRef.current = { symbol: currentSymbol, inList };
                  applyWatchlistBtnState(watchlistBtnRef.current, inList);
                  return;
                }
                optimisticShortcutStateRef.current = null;
              })
              .catch(() => {
                optimisticShortcutStateRef.current = { symbol: currentSymbol, inList };
                applyWatchlistBtnState(watchlistBtnRef.current, inList);
              })
              .finally(() => {
                watchlistShortcutPendingRef.current = false;
              });
          });
        }
        if (onToggleFlagShortcutRef.current) {
          widget.onShortcut('alt+b', () => {
            if (flagShortcutPendingRef.current) return;
            const { symbol: currentSymbol } = widget.symbolInterval();
            flagShortcutPendingRef.current = true;
            void Promise.resolve(onToggleFlagShortcutRef.current?.(currentSymbol))
              .catch(() => {})
              .finally(() => {
                flagShortcutPendingRef.current = false;
              });
          });
        }
        widget.chart().onIntervalChanged?.().subscribe(null, (interval) => onIntervalChangeRef.current?.(interval));
        widget.chart().onSymbolChanged().subscribe(null, () => {
          const { symbol: newSymbol } = widget.symbolInterval();
          onSymbolChangeRef.current?.(newSymbol);
        });

        if (options.custom_indicators_getter) {
          widget.headerReady().then(() => {
            if (cancelled) return;
            widget.createDropdown({
              title: 'Custom',
              tooltip: 'Add custom indicators',
              items: CUSTOM_INDICATOR_NAMES.map((name) => ({
                title: name,
                onSelect: () => widget.chart().createStudy(name, false, false),
              })),
            });
          }).catch(() => {});
        }

        if (onAddToWatchlistRef.current || onRemoveFromWatchlistRef.current || onToggleFlagShortcutRef.current || onSetFlagColorRef.current) {
          widget.headerReady().then(() => {
            if (cancelled) return;

            const { symbol } = widget.symbolInterval();

            if (onToggleFlagShortcutRef.current || onSetFlagColorRef.current) {
              const flagBtn = widget.createButton({ align: 'right' });
              flagBtn.classList.add('tv-flag-btn');
              updateFlagBtn(flagBtn, symbol, flaggedColorsRef.current);
              flagBtn.addEventListener('click', () => {
                const { symbol: currentSymbol } = widget.symbolInterval();
                toggleFlagMenu(flagBtn, currentSymbol, (color) => {
                  const previousColor = findSymbolFlagColor(currentSymbol, flaggedColorsRef.current);
                  optimisticFlagStateRef.current = { symbol: currentSymbol, flagColor: color };
                  applyFlagBtnState(flagBtn, color);
                  void Promise.resolve(onSetFlagColorRef.current?.(currentSymbol, color))
                    .then((ok) => {
                      if (ok === false) {
                        optimisticFlagStateRef.current = { symbol: currentSymbol, flagColor: previousColor };
                        applyFlagBtnState(flagBtn, previousColor);
                        return;
                      }
                      optimisticFlagStateRef.current = null;
                    })
                    .catch(() => {
                      optimisticFlagStateRef.current = { symbol: currentSymbol, flagColor: previousColor };
                      applyFlagBtnState(flagBtn, previousColor);
                    });
                }, flagMenuRef, options.container instanceof HTMLElement ? options.container.getBoundingClientRect() : undefined);
              });
              flagBtnRef.current = flagBtn;
            }

            if (onAddToWatchlistRef.current || onRemoveFromWatchlistRef.current) {
              const btn = widget.createButton({ align: 'right' });
              btn.classList.add('tv-watchlist-btn');
              updateWatchlistBtn(btn, symbol, watchlistNamesRef.current);
              btn.addEventListener('click', () => {
              const { symbol: currentSymbol } = widget.symbolInterval();
              if (isSymbolInWatchlist(currentSymbol, watchlistNamesRef.current)) {
                btn.innerHTML = STAR_OUTLINED;
                btn.classList.remove('tv-watchlist-btn--active');
                btn.title = 'Add to watchlist';
                onRemoveFromWatchlistRef.current?.(currentSymbol);
                return;
              }
              btn.innerHTML = STAR_FILLED;
              btn.classList.add('tv-watchlist-btn--active');
              btn.title = 'Remove from watchlist';
              onAddToWatchlistRef.current?.(currentSymbol);
              });
              watchlistBtnRef.current = btn;
            }

            widget.chart().onSymbolChanged().subscribe(null, () => {
              const { symbol: newSymbol } = widget.symbolInterval();
              updateWatchlistBtn(watchlistBtnRef.current, newSymbol, watchlistNamesRef.current);
              updateFlagBtn(flagBtnRef.current, newSymbol, flaggedColorsRef.current);
            });
          }).catch(() => {});
        }

        onReadyRef.current?.();
      });

      window.addEventListener('pagehide', flushSettings);
      window.addEventListener('beforeunload', flushSettings);
      window.addEventListener('pagehide', persistLayout);
      window.addEventListener('beforeunload', persistLayout);

      cleanupRef.current = () => {
        window.removeEventListener('pagehide', flushSettings);
        window.removeEventListener('beforeunload', flushSettings);
        window.removeEventListener('pagehide', persistLayout);
        window.removeEventListener('beforeunload', persistLayout);
        clearAlarmLines(alarmLinesRef.current);
        closeFlagMenu(flagMenuRef);
        clearDraftAlarmLine(draftAlarmLineRef);
        widget.remove();
        widgetRef.current = null;
        watchlistBtnRef.current = null;
        flagBtnRef.current = null;
      };
    }).catch((err) => {
      if (!cancelled) {
        console.error(err);
      }
    });

    return () => {
      cancelled = true;
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [options?.container]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;
    syncAlarmLines(widget, alarms, alarmLinesRef.current, (id) => onDeleteAlarmRef.current?.(id));
  }, [alarms]);

  useEffect(() => {
    const widget = widgetRef.current;
    syncDraftAlarmLine(widget, draftAlarmLineRef, draftAlarmPrice, (price) => onDraftAlarmPriceChangeRef.current?.(price));
  }, [draftAlarmPrice]);

  useEffect(() => {
    const btn = watchlistBtnRef.current;
    if (!btn || !widgetRef.current) return;
    const { symbol } = widgetRef.current.symbolInterval();
    const optimistic = optimisticShortcutStateRef.current;
    if (optimistic && optimistic.symbol === symbol) {
      applyWatchlistBtnState(btn, optimistic.inList);
      return;
    }
    updateWatchlistBtn(btn, symbol, watchlistSymbolNames);
  }, [watchlistSymbolNames]);

  useEffect(() => {
    const btn = flagBtnRef.current;
    if (!btn || !widgetRef.current) return;
    const { symbol } = widgetRef.current.symbolInterval();
    const optimistic = optimisticFlagStateRef.current;
    if (optimistic && optimistic.symbol === symbol) {
      applyFlagBtnState(btn, optimistic.flagColor);
      return;
    }
    updateFlagBtn(btn, symbol, flaggedWatchlistSymbolColors);
  }, [flaggedWatchlistSymbolColors]);

  return widgetRef;
}

function syncDraftAlarmLine(widget: TradingViewWidget | null, draftLineRef: MutableRefObject<OrderLineAdapter | null>, price: number | null | undefined, onMove: (price: number) => void) {
  if (!widget || price === null || price === undefined || !Number.isFinite(price) || price <= 0) {
    clearDraftAlarmLine(draftLineRef);
    return;
  }

  if (!draftLineRef.current) {
    try {
      const line = widget.chart().createOrderLine({ disableUndo: true });
      line
        .setPrice(price)
        .setText('Draft alarm')
        .setQuantity('')
        .setTooltip('Drag to adjust alarm price')
        .setEditable(true)
        .setCancellable(false)
        .setLineColor('#60a5fa')
        .setLineStyle(2)
        .setLineWidth(2)
        .setLineLength(100)
        .setBodyBackgroundColor('rgba(37, 99, 235, 0.22)')
        .setBodyTextColor('#dbeafe')
        .setBodyBorderColor('rgba(96, 165, 250, 0.75)')
        .setQuantityBackgroundColor('rgba(37, 99, 235, 0)')
        .setQuantityTextColor('rgba(219, 234, 254, 0)')
        .setQuantityBorderColor('rgba(37, 99, 235, 0)');
      line.onMove(() => onMove(line.getPrice()));
      draftLineRef.current = line;
    } catch (err) {
      console.error('draft alarm line:', err);
      return;
    }
  }

  draftLineRef.current.setPrice(price);
}

function clearDraftAlarmLine(draftLineRef: MutableRefObject<OrderLineAdapter | null>) {
  draftLineRef.current?.remove();
  draftLineRef.current = null;
}

function syncAlarmLines(widget: TradingViewWidget, alarms: PriceAlarm[], currentLines: Map<string, OrderLineAdapter>, onDeleteAlarm: (id: string) => void) {
  const activeIds = new Set(alarms.map((alarm) => alarm.id));
  for (const [id, line] of currentLines) {
    if (!activeIds.has(id)) {
      line.remove();
      currentLines.delete(id);
    }
  }

  for (const alarm of alarms) {
    if (!Number.isFinite(alarm.target_price) || alarm.target_price <= 0) continue;
    const existing = currentLines.get(alarm.id);
    if (existing) {
      styleAlarmLine(existing, alarm);
      continue;
    }
    try {
      const line = widget.chart().createOrderLine({ disableUndo: true });
      line.onCancel(alarm.id, onDeleteAlarm);
      styleAlarmLine(line, alarm);
      currentLines.set(alarm.id, line);
    } catch (err) {
      console.error('alarm line:', err);
    }
  }
}

function styleAlarmLine(line: OrderLineAdapter, alarm: PriceAlarm) {
  const color = '#8b8f9b';
  line
    .setPrice(alarm.target_price)
    .setText('')
    .setQuantity('')
    .setTooltip(`${alarm.symbol} ${alarm.direction} ${formatAlarmPrice(alarm.target_price)}`)
    .setCancelTooltip('Delete alarm')
    .setEditable(false)
    .setCancellable(true)
    .setLineColor(color)
    .setLineLength(1)
    .setLineWidth(1)
    .setBodyBackgroundColor('rgba(7, 10, 18, 0)')
    .setBodyTextColor('rgba(244, 244, 245, 0)')
    .setBodyBorderColor('rgba(7, 10, 18, 0)')
    .setQuantityBackgroundColor('rgba(7, 10, 18, 0)')
    .setQuantityTextColor('rgba(244, 244, 245, 0)')
    .setQuantityBorderColor('rgba(7, 10, 18, 0)')
    .setCancelButtonBackgroundColor('rgba(7, 10, 18, 0.72)')
    .setCancelButtonBorderColor('rgba(139, 143, 155, 0.55)')
    .setCancelButtonIconColor('#d4d4d8');
}

function clearAlarmLines(lines: Map<string, OrderLineAdapter>) {
  for (const line of lines.values()) {
    line.remove();
  }
  lines.clear();
}

function formatAlarmPrice(price: number): string {
  return price >= 100 ? price.toFixed(2) : price >= 1 ? price.toFixed(4).replace(/0+$/, '').replace(/\.$/, '') : price.toPrecision(4);
}
